import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { boolean, object, string } from "yup";
import type { CompanyStoreSetting } from "~/models/CompanyStoreSetting";
import type { ConstructionItemSetting } from "./ConstructionItemSetting";
import type { InspectionBy } from "./Deal";

export type CompanySetting = {
	antisocialCheckId: string | null;
	authId: number;
	autoFillEstimateAddressType: "none" | "company_name";
	baseName: string;
	ceoFirstName: string;
	ceoLastName: string;
	companyMasterId: number;
	defaultInspectionBy: InspectionBy;
	fromMarket: boolean;
	managedUnitNumber: string;
	marketPlan: "single" | "multiple" | null;
	name: string;
	onlyAmaterasProperties: boolean;
	settlementEnabled: boolean;
	syncInspectionDateToKt: boolean;
	useIaStaffLogin: boolean;
	useV2DealDetail: boolean;
};

export type CompanySettingEdit = Pick<
	CompanySetting,
	| "name"
	| "ceoLastName"
	| "ceoFirstName"
	| "managedUnitNumber"
	| "antisocialCheckId"
	| "autoFillEstimateAddressType"
	| "defaultInspectionBy"
>;

export type CompanySettingWithStore = CompanySetting & {
	companyStoreSettings: CompanyStoreSetting[];
};

// vendorのみcompanySettingからconstructionItemSettingsを表示させたい時
export type CompanySettingWithItemSetting = CompanySetting & {
	constructionItemSettings: ConstructionItemSetting[];
};

export type DealCompanySetting = Pick<
	CompanySetting,
	"companyMasterId" | "name"
>;

export const getItemSettingLength = (
	itemSettings: DeepReadonly<ConstructionItemSetting[]>,
	vendorCompanyId: number
): number =>
	itemSettings.filter(
		(itemSetting: ConstructionItemSetting) =>
			itemSetting.vendorCompanyId === vendorCompanyId
	).length;

export const convertToComboBoxOption = (
	companySetting: DealCompanySetting
): { label: string; value: string } => ({
	label: companySetting.name,
	value: String(companySetting.companyMasterId)
});

export type MarketInitialCompanySetting = {
	ceoFirstName: string;
	ceoLastName: string;
	companyName: string;
	email: string;
	existSetting: boolean;
	managedUnitNumber: string;
	phoneNumber: string;
	state: string;
	zipcode: string;
};

export type MarketCompanySetting = {
	ceoFirstName: string;
	ceoLastName: string;
	checkTerm: boolean;
	city: string;
	companyName: string;
	email: string;
	managedUnitNumber: string;
	marketPlan: "single" | "multiple" | null;
	otherAddress: string;
	phoneNumber: string;
	staffEmail: string;
	staffName: string;
	staffPhoneNumber: string;
	state: string | null;
	zipcode: string;
};

export const validationSchemaMarketCompanySetting = object().shape({
	ceoFirstName: string().required("入力してください"),
	ceoLastName: string().required("入力してください"),
	checkTerm: boolean().isTrue("チェックしてください"),
	city: string().required("入力してください"),
	companyName: string().required("入力してください"),
	email: string()
		.email("メールアドレスの形式を確認してください")
		.required("入力してください"),
	managedUnitNumber: string().required("入力してください"),
	marketPlan: string().nullable().required("入力してください"),
	staffEmail: string()
		.email("メールアドレスの形式を確認してください")
		.required("入力してください"),
	staffName: string().required("入力してください"),
	staffPhoneNumber: string().required("入力してください"),
	state: string().required("入力してください"),
	zipcode: string().required("入力してください")
});

export type AdminSettingCompanySetting = {
	fromMarket: boolean;
	onlyAmaterasProperties: boolean;
	robotPaymentCode: string;
	settlementEnabled: boolean;
	syncInspectionDateToKt: boolean;
	useIaStaffLogin: boolean;
	useV2DealDetail: boolean;
};

export const validationSchemaCompanySetting = object().shape({
	autoFillEstimateAddressType: string().required("入力してください"),
	defaultInspectionBy: string().required("入力してください"),
	name: string().required("入力してください")
});
